import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeftIcon, ChevronRightIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import type { EbookMove, EbookStudyReplay } from '@wx-weiqi/contracts';
import type { EbookClient } from '../services/ebooks';
import { ebookMoveLabels } from '../services/ebook-variations';
import { ebookStudyGradeCommand, type EbookStudyGrade } from '../services/ebook-study-grading';
import { boardCoordinate, stoneRadius } from '../domain/board-geometry';
import { boardViewportForCorner, type HomeworkBoardCorner } from '../domain/homework-board-focus';
import { BoardSurface } from './BoardSurface';
import { ClassroomZoomControl } from './ClassroomZoomControl';
import { EbookBranchTrails } from './EbookBranchTrails';
import { StudyResult, studyDuration, studyTime } from './EbookStudyPresentation';
import './ebook-records-workspace.css';

const MOVES_PER_PAGE = 5;

function moveName(move: EbookMove, size: number) {
  return `${move.color === 'BLACK' ? '黑' : '白'} ${move.point ? `${'ABCDEFGHJKLMNOPQRST'[move.point.x]}${size - move.point.y}` : '虚着'}`;
}
export function EbookStudyReplayDialog({
  client,
  documentId,
  attemptId,
  onClose,
  onChanged,
}: {
  client: EbookClient;
  documentId: string;
  attemptId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const saveInFlight = useRef(false);
  const [data, setData] = useState<EbookStudyReplay | null>(null);
  const [ply, setPly] = useState(0);
  const [movePage, setMovePage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showHints, setShowHints] = useState(true);
  const [corner, setCorner] = useState<HomeworkBoardCorner | null>(null);
  const [draftGrade, setDraftGrade] = useState<EbookStudyGrade | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = draftGrade !== null && draftGrade !== data?.record.status;
  const requestClose = () => {
    if (saveInFlight.current) return;
    if (dirty) setConfirmClose(true);
    else onClose();
  };
  useEffect(() => {
    const el = dialog.current;
    const previous = document.activeElement;
    el?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      el?.close();
      document.body.style.overflow = overflow;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
      else document.querySelector<HTMLElement>('.ebook-study-list-heading button')?.focus();
    };
  }, []);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    void client
      .studyReplay(documentId, attemptId, ply)
      .then((result) => {
        if (active) {
          setData(result);
          setMovePage(Math.max(0, Math.floor((result.position.ply - 1) / MOVES_PER_PAGE)));
        }
      })
      .catch((e: unknown) => {
        if (active) setError(userErrorMessage(e, '回放加载失败，请重试'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [client, documentId, attemptId, ply, refresh]);
  const grade = async () => {
    if (!data || !data.record.submitted || !draftGrade || !dirty || loading || saveInFlight.current)
      return;
    const command = ebookStudyGradeCommand(data.record, draftGrade);
    if (!command) return;
    saveInFlight.current = true;
    setGrading(true);
    setError('');
    setNotice('');
    try {
      const saved = await client.attempt(data.record.releaseId, command);
      setData((current) =>
        current
          ? {
              ...current,
              record: {
                ...current.record,
                status: saved.status,
                stateVersion: saved.stateVersion,
              },
            }
          : current,
      );
      setDraftGrade(null);
      setNotice('批改已保存');
      setRefresh((n) => n + 1);
      onChanged();
    } catch (e) {
      setError(userErrorMessage(e, '批改失败，请刷新记录后重试'));
    } finally {
      saveInFlight.current = false;
      setGrading(false);
    }
  };
  const labels = data
    ? ebookMoveLabels(
        data.question.size,
        data.position.board.stones,
        data.moves.slice(0, data.position.ply),
      )
    : [];
  const next = data?.playedNext;
  const blocked = loading || grading;
  const selectedGrade = draftGrade ?? data?.record.status;
  const movePages = Math.max(1, Math.ceil((data?.moves.length ?? 0) / MOVES_PER_PAGE));
  return createPortal(
    <dialog
      ref={dialog}
      className="ebook-study-dialog ebook-teacher ebook-replay-workspace"
      aria-labelledby="ebook-replay-title"
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
    >
      <header className="ebook-replay-header">
        <button autoFocus onClick={requestClose} disabled={grading}>
          <ChevronLeftIcon aria-hidden="true" />
          返回学习记录
        </button>
        <div className="ebook-replay-identity">
          <h2 id="ebook-replay-title">
            {data ? `${data.record.studentName} · ${data.record.questionLabel}` : '作答回放'}
          </h2>
          {data && (
            <p>
              第 {data.record.attemptNumber} 次练习 · 用时 {studyDuration(data.record.durationMs)}
            </p>
          )}
        </div>
        <span className="ebook-replay-save-state" data-dirty={dirty} role="status">
          {grading ? '正在保存…' : dirty ? '有未保存修改' : notice}
        </span>
      </header>
      {confirmClose && (
        <div className="ebook-replay-leave-warning" role="alert">
          <strong>本次批改尚未保存</strong>
          <span>返回后将放弃刚选择的结果。</span>
          <button onClick={() => setConfirmClose(false)}>继续批改</button>
          <button onClick={onClose}>放弃并返回</button>
        </div>
      )}
      {error && (
        <div className="ebook-error" role="alert">
          {error}
          <button disabled={blocked} onClick={() => setRefresh((n) => n + 1)}>
            刷新记录
          </button>
        </div>
      )}
      {!data ? (
        <p className="ebook-replay-loading" role="status">
          {loading ? '正在读取原题和作答过程…' : '暂时无法打开回放，请刷新记录。'}
        </p>
      ) : (
        <>
          <div className="ebook-replay-context">
            <h3>作答回放</h3>
            <StudyResult status={data.record.status} />
            <span title={data.question.text || data.question.goal}>
              {data.question.text || data.question.goal || '未填写题目要求'}
            </span>
          </div>
          <div className="ebook-replay-layout" aria-busy={blocked}>
            <aside className="ebook-replay-moves">
              <h3>
                落子顺序 <span>共 {data.position.total} 手</span>
              </h3>
              <div className="ebook-replay-move-list" role="group" aria-label="落子顺序">
                <button
                  aria-current={data.position.ply === 0 ? 'step' : undefined}
                  disabled={blocked}
                  onClick={() => setPly(0)}
                >
                  初始局面
                </button>
                {data.moves
                  .slice(movePage * MOVES_PER_PAGE, (movePage + 1) * MOVES_PER_PAGE)
                  .map((move, index) => {
                    const movePly = movePage * MOVES_PER_PAGE + index + 1;
                    return (
                      <button
                        key={movePly}
                        aria-current={data.position.ply === movePly ? 'step' : undefined}
                        disabled={blocked}
                        onClick={() => setPly(movePly)}
                      >
                        <span>{movePly}</span>
                        <strong>{moveName(move, data.question.size)}</strong>
                        <small>
                          {data.question.mode === 'MANUAL' ||
                          move.color === data.question.currentPlayer
                            ? '学生'
                            : '应对'}
                        </small>
                      </button>
                    );
                  })}
              </div>
              <div className="ebook-replay-move-pages">
                <button
                  aria-label="上一页落子"
                  disabled={blocked || movePage === 0}
                  onClick={() => setMovePage((page) => page - 1)}
                >
                  <ChevronLeftIcon aria-hidden="true" />
                </button>
                <span>
                  {movePage + 1} / {movePages}
                </span>
                <button
                  aria-label="下一页落子"
                  disabled={blocked || movePage + 1 >= movePages}
                  onClick={() => setMovePage((page) => page + 1)}
                >
                  <ChevronRightIcon aria-hidden="true" />
                </button>
              </div>
              <p className="ebook-study-footnote">点击任一手，查看当时的棋盘。</p>
            </aside>
            <div className="ebook-replay-center">
              <div className="ebook-replay-controls">
                <button disabled={blocked || data.position.ply === 0} onClick={() => setPly(0)}>
                  初始
                </button>
                <button
                  disabled={blocked || data.position.ply === 0}
                  onClick={() => setPly(data.position.ply - 1)}
                >
                  <ChevronLeftIcon aria-hidden="true" />
                  上一手
                </button>
                <span aria-live="polite">
                  第 {data.position.ply} / {data.position.total} 手
                </span>
                <button
                  disabled={blocked || data.position.ply === data.position.total}
                  onClick={() => setPly(data.position.ply + 1)}
                >
                  下一手
                  <ChevronRightIcon aria-hidden="true" />
                </button>
                <button
                  disabled={blocked || data.position.ply === data.position.total}
                  onClick={() => setPly(data.position.total)}
                >
                  最后
                </button>
                <ClassroomZoomControl
                  zoomed={corner !== null}
                  onRestore={() => setCorner(null)}
                  onSelect={setCorner}
                />
              </div>
              <div className="ebook-replay-board-area">
                <div className="ebook-board ebook-replay-board">
                  <BoardSurface
                    boardAppearance="wood"
                    stoneAppearance="shell"
                    viewport={corner === null ? null : boardViewportForCorner(corner)}
                    size={data.question.size}
                    stones={data.position.board.stones}
                    lastMove={null}
                    stoneLabels={labels}
                    initialAnnotations={data.question.marks
                      .filter(
                        (mark) =>
                          !labels.some(
                            (label) =>
                              label.point.x === mark.point.x && label.point.y === mark.point.y,
                          ),
                      )
                      .map((mark) =>
                        mark.kind === 'LABEL'
                          ? { point: mark.point, kind: 'LABEL' as const, text: mark.label }
                          : { point: mark.point, kind: mark.kind },
                      )}
                    svgOverlay={
                      <>
                        {showHints && (
                          <EbookBranchTrails
                            size={data.question.size}
                            stones={data.position.board.stones}
                            moves={data.nextMoves}
                          />
                        )}
                        {next?.move.point && (
                          <circle
                            className="ebook-replay-actual"
                            role="img"
                            aria-label={next.byStudent ? '学生实际落点' : '系统实际应对'}
                            cx={boardCoordinate(data.question.size, next.move.point.x)}
                            cy={boardCoordinate(data.question.size, next.move.point.y)}
                            r={stoneRadius(data.question.size) * 0.86}
                            fill="none"
                            stroke="#273e38"
                            strokeWidth={2.5}
                            pointerEvents="none"
                          >
                            <title>{next.byStudent ? '学生实际落点' : '系统实际应对'}</title>
                          </circle>
                        )}
                      </>
                    }
                  />
                </div>
              </div>
              <p className="ebook-replay-next" aria-live="polite">
                {loading ? (
                  '正在回放…'
                ) : next ? (
                  <>
                    {next.byStudent ? '学生下一手' : '系统下一手'}：
                    {moveName(next.move, data.question.size)}
                    <span data-outcome={next.outcome}>
                      {next.outcome === 'CORRECT'
                        ? '可达正确答案'
                        : next.outcome === 'INCORRECT'
                          ? '错误落点'
                          : '未判定'}
                    </span>
                  </>
                ) : (
                  '已到本次作答末尾'
                )}
              </p>
            </div>
            <aside className="ebook-replay-details">
              <div className="ebook-replay-grading">
                <h3>批改本次作答</h3>
                <p>
                  {data.record.submitted
                    ? '回放后选择结果，再保存批改。'
                    : '学生尚未提交，暂不能批改。'}
                </p>
                <fieldset disabled={blocked || !data.record.submitted}>
                  <legend className="ebook-sr-only">批改结果</legend>
                  {(['CORRECT', 'INCORRECT'] as const).map((status) => (
                    <label
                      key={status}
                      className="ebook-replay-grade-choice"
                      data-selected={selectedGrade === status}
                    >
                      <input
                        type="radio"
                        name="ebook-replay-grade"
                        value={status}
                        checked={selectedGrade === status}
                        onChange={() => {
                          setDraftGrade(status);
                          setNotice('');
                        }}
                      />
                      <StudyResult status={status} />
                    </label>
                  ))}
                </fieldset>
              </div>
              <div className="ebook-replay-record-info">
                <h3>记录信息</h3>
                <dl>
                  <dt>作答方式</dt>
                  <dd>{data.question.mode === 'MANUAL' ? '手动' : '自动'}</dd>
                  <dt>提交状态</dt>
                  <dd>{data.record.submitted ? '已提交' : '未提交'}</dd>
                  <dt>当前结果</dt>
                  <dd>
                    <StudyResult status={data.record.status} />
                  </dd>
                </dl>
                <details className="ebook-replay-more">
                  <summary>提示与详情</summary>
                  <label className="ebook-replay-legend">
                    <input
                      type="checkbox"
                      checked={showHints}
                      onChange={(event) => setShowHints(event.target.checked)}
                    />
                    显示下一手提示
                  </label>
                  <p>绿点为正确，红点为错误，空心圈是实际落点。</p>
                  <p>{data.question.text || data.question.goal || '未填写题目要求'}</p>
                  <dl>
                    <dt>开始时间</dt>
                    <dd>{studyTime(data.record.startedAt)}</dd>
                    <dt>完成时间</dt>
                    <dd>{studyTime(data.record.finishedAt)}</dd>
                  </dl>
                  {data.record.durationMs === null && (
                    <p>{data.record.submitted ? '此历史记录未保存完整计时。' : '本次尚未完成。'}</p>
                  )}
                  {data.record.hinted && <p>本次使用过提示</p>}
                  {data.record.demonstrated && <p>本次查看过答案</p>}
                </details>
              </div>
              <p className="ebook-replay-snapshot-note">
                <InfoCircledIcon aria-hidden="true" />
                按学生作答时的题目与答案回放。
              </p>
            </aside>
          </div>
          <footer className="ebook-replay-action-bar">
            <span>
              <InfoCircledIcon aria-hidden="true" />
              批改只影响本次作答记录
            </span>
            <div>
              <button disabled={grading} onClick={requestClose}>
                返回记录
              </button>
              <button
                className="primary"
                disabled={blocked || !data.record.submitted || !dirty}
                onClick={() => {
                  void grade();
                }}
              >
                {grading ? '正在保存…' : '保存批改'}
              </button>
            </div>
          </footer>
        </>
      )}
    </dialog>,
    document.body,
  );
}
